import { View, StyleSheet, Pressable, Modal } from "react-native";
import { useState, useEffect, useMemo, useCallback, useRef, StrictMode } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { SystemBars } from "./components/SystemBars/SystemBars";
import LoadingIndicator from "./sub/components/LoadingIndicator";
import ErrorMessage from "./sub/components/ErrorMessage";
import NotificationSubscription from "./components/HomeComponents/NotificationSubscription";
import AlertMessage from "./components/HomeComponents/AlertMessage";
import AppStore from "./storage/appStore";
import RootNavigator from "./navigation/RootNavigator";
import ChatBot from "./components/ChatBot";
import { subscribeGarbagePush } from "./utils/api/subscriptionApi";
import { platform, os, logError, initFirebase } from "./platform";
import { useInitPushNotifications } from "./sub/utils/pushNotifications";
import InstallPromptWeb from "./components/InstallPromptWeb";
import { STRINGS } from "./constants/strings";
import { getApiKey } from "./utils/api/appDataApi";

// Memoized linking configuration to prevent re-creation on every render
const links = {
  prefixes: ["vbconnect://"],
  config: {
    screens: {
      Home: "",
      PayBills: "pay-bills",
      Links: "links",
      ChatBot: "chat",
      SettingsTab: {
        screens: {
          Settings: "settings",
          Terms: "terms",
        },
      },
    },
  },
};

/**
 * Root application component for VBConnect.
 *
 * Manages:
 * - Push notification setup and subscriptions (native only)
 * - Global error and alert messaging
 * - Chat modal state and rendering
 * - Navigation container with deep linking (native only)
 * - Platform-specific UI rendering (web vs native)
 *
 * The component orchestrates several key responsibilities:
 * 1. **Push Notifications**: Initializes Firebase Cloud Messaging on native platforms,
 *    manages notification tokens, and subscribes to garbage day and news release notifications.
 * 2. **Global State**: Connects to Zustand store for loading, error, alert, and notification states.
 * 3. **Chat Modal**: Renders a platform-specific modal (web: div overlay, native: Modal component)
 *    for the ChatBot component with proper z-index layering.
 * 4. **Navigation**: Provides NavigationContainer with deep linking configuration for native platforms.
 * 5. **Error Handling**: Displays global error messages and handles dismissal.
 * 6. **Web PWA**: Manages install prompt display for web platform.
 *
 * @component
 * @returns {JSX.Element} The root app component with providers and navigation
 *
 * @example
 * // App is typically rendered at the entry point (index.web.js or index.js)
 * import App from './App';
 * ReactDOM.render(<App />, document.getElementById('root'));
 */
const App = () => {
  const loading = AppStore((state) => state.loading);
  const errorMessage = AppStore((state) => state.errorMessage);
  const notificationSubscription = AppStore((state) => state.notificationSubscription);
  const showAlert = AppStore((state) => state.showAlert);
  const alertMessage = AppStore((state) => state.alertMessage);
  const alertOkFn = AppStore((state) => state.alertOkFn);
  const setNotificationSubscription = AppStore((state) => state.setNotificationSubscription);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const setShowAlert = AppStore((state) => state.setShowAlert);
  const notificationToken = AppStore((state) => state.notificationToken);
  const setNotificationToken = AppStore((state) => state.setNotificationToken);
  const propertyInfo = AppStore((state) => state.propertyInfo);
  const webInstaller = AppStore((state) => state.webInstaller);
  const firebaseApiKey = AppStore((state) => state.firebaseApiKey);
  const setFirebaseApiKey = AppStore((state) => state.setFirebaseApiKey);
  const [showError, setShowError] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);

  const unsubscribeRef = useRef(null);

  const garbageDay = propertyInfo?.garbage?.garbageDay;

  // Suppress pointerEvents deprecation warning from third-party libraries
  useEffect(() => {
    if (platform === "web") {
      const originalWarn = console.warn;
      console.warn = (...args) => {
        if (
          args[0]?.includes?.("pointerEvents is deprecated") ||
          args[0]?.includes?.("google.maps.Marker is deprecated")
        ) {
          return;
        }
        originalWarn(...args);
      };
    }
  }, []);

  // Hide notification subscription and alert message on web refresh.
  useEffect(() => {
    if (platform === "web") {
      setNotificationSubscription({ show: false, type: "" });
      setShowAlert(false);
    }
  }, [setNotificationSubscription, setShowAlert]); // Only run once on mount

  /**
   * The objects needed by the push notification init submodule function
   * Web and native require different objects
   */
  const firebaseInit = useMemo(
    () =>
      platform === "web"
        ? {}
        : {
            unsubscribeRef,
            setNotificationToken,
            notificationToken,
          },
    [notificationToken, setNotificationToken]
  );

  useInitPushNotifications(platform, "news_releases", firebaseInit);

  // Subscribe to garbage day notifications when token and garbageDay are available
  useEffect(() => {
    if (!notificationToken || !garbageDay) return;

    (async () => {
      try {
        await subscribeGarbagePush(notificationToken, garbageDay);
      } catch (error) {
        logError("garbage_notification_post_failed", {
          message: error.message,
          stack: error.stack,
          garbageDay,
          hasToken: !!notificationToken,
        });
      }
    })();
  }, [notificationToken, garbageDay]);

  useEffect(() => {
    setShowError(!loading && errorMessage !== "");
  }, [loading, errorMessage]);

  useEffect(() => {
    const fetchFirebaseKey = async () => {
      if (os === "web" && firebaseApiKey === null) {
        try {
          const response = await getApiKey("firebase");
          setFirebaseApiKey(response.key);
          initFirebase(response.key);
        } catch (error) {
          setErrorMessage(STRINGS.failedToLoadData);
        }
      } else if (os === "web" && firebaseApiKey) {
        try {
          initFirebase(firebaseApiKey);
        } catch (error) {
          // Key may be expired/rotated — clear it to trigger a re-fetch
          setFirebaseApiKey(null);
        }
      }
    };

    fetchFirebaseKey();
  }, [firebaseApiKey, setFirebaseApiKey, setErrorMessage]);

  // Memoize callbacks to prevent re-renders
  const handleCloseChat = useCallback(() => {
    setShowChatModal(false);
  }, []);

  const handleErrorClose = useCallback(() => {
    setShowError(false);
    setErrorMessage("");
  }, [setErrorMessage]);

  const handleAlertClosePress = useCallback(() => {
    setShowAlert(false);
  }, [setShowAlert]);

  // Memoize linking config for NavigationContainer
  const linkingConfig = useMemo(() => (platform === "native" ? links : undefined), []);

  return (
    <StrictMode>
      <SafeAreaProvider>
        <SystemBars style="dark" />
        <LoadingIndicator isLoading={loading} />
        <ErrorMessage message={errorMessage} showError={showError} onClose={handleErrorClose} />
        <AlertMessage
          message={alertMessage}
          showAlert={showAlert}
          okOnPress={alertOkFn}
          closeOnPress={handleAlertClosePress}
        />
        <NavigationContainer linking={linkingConfig}>
          {notificationSubscription.show && <NotificationSubscription />}
          <View style={styles.container}>
            <RootNavigator />
          </View>
        </NavigationContainer>

        {/* Chat Modal - Rendered at root level for proper z-index */}
        {showChatModal &&
          (platform === "web" ? (
            <>
              <div style={styles.chatBackdrop} onClick={handleCloseChat} />
              <div style={styles.chatPopover}>
                <ChatBot isModal={true} onClose={handleCloseChat} />
              </div>
            </>
          ) : (
            <Modal visible={showChatModal} transparent={true} animationType="fade" onRequestClose={handleCloseChat}>
              <Pressable style={styles.chatBackdrop} onPress={handleCloseChat}>
                <View style={styles.chatPopover} onStartShouldSetResponder={() => true}>
                  <ChatBot isModal={true} onClose={handleCloseChat} />
                </View>
              </Pressable>
            </Modal>
          ))}
        {os === "web" && !webInstaller.installed && !webInstaller.installerDismissed && <InstallPromptWeb />}
      </SafeAreaProvider>
    </StrictMode>
  );
};

/**
 * StyleSheet for App component layout and chat modal styling.
 *
 * Defines platform-specific styles for:
 * - **container**: Main flex container for the app layout
 * - **chatBackdrop**: Semi-transparent overlay behind the chat modal
 *   - Web: Fixed positioning with full viewport coverage
 *   - Native: Flex-based with centered content
 * - **chatPopover**: Chat modal container with responsive sizing
 *   - Web: Fixed positioning, centered with transform, max-width 520px
 *   - Native: Flex-based with shadow effects
 *
 * @type {Object}
 * @property {Object} container - Root container with flex layout
 * @property {Object} chatBackdrop - Backdrop overlay (platform-specific)
 * @property {Object} chatPopover - Chat modal container (platform-specific)
 */
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  chatBackdrop:
    platform === "web"
      ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9998,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }
      : {
          flex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          justifyContent: "center",
          alignItems: "center",
        },
  chatPopover:
    platform === "web"
      ? {
          position: "fixed",
          width: "95%",
          height: "554px",
          maxWidth: "520px",
          maxHeight: "554px",
          minHeight: "300px",
          padding: 0,
          margin: 0,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          backgroundColor: "white",
          borderWidth: "1px",
          borderColor: "#ccc",
          borderStyle: "solid",
          borderRadius: "10px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        }
      : {
          width: "95%",
          height: 554,
          maxWidth: 520,
          maxHeight: 554,
          minHeight: 300,
          backgroundColor: "white",
          borderRadius: 10,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.1,
          shadowRadius: 10,
          overflow: "hidden",
        },
});

export default App;
